// eslint-disable-next-line td-wb-rules/uirouter-import-rule
import { Ng2StateDeclaration, Transition, UIRouter } from '@uirouter/angular';

import {
  MessagesModalComponent,
  UnsupportedBrowserComponent
} from '@tdwb/core';
import { BaseRoutes, type LazyLoad, type StatesList } from '@tdwb/webbroker/core';
import { IntegrationComponent } from './modules/platform/integration/integration.module';
import type { SecureMessagesModule } from './modules/platform/secure-messages/secure-messages.module';
import type { OrderEntryModule } from './modules/trading/order-entry-reorg/order-entry.module';
import type { AdEnrollModule } from './modules/ad-enroll/ad-enroll.module';
import type { UserProfileModule } from './modules/user-profile/user-profile.module';
import type { FractionalModule } from './modules/trading/fractional/fractional.module';
import type { GicPageModule } from './modules/trading/gic/gic-page.module';
import type { OrderStatusModule } from './modules/trading/order-status/order-status.module';
import type { WebbrokerCoreE2EModule } from './modules/core/sandbox/webbroker-core-e2e.module';
import type { LearnModule } from './modules/learn/learn.module';
import type { EservicesModule } from './modules/eservices/eservices.module';
import type { TransfersModule } from './modules/transfers/transfers.module';
import type { NewUserModule } from './modules/platform/new-user/new-user.module';
import type { AttraderModule } from './modules/platform/preferences/attrader/attrader.module';
import type { ErrorsModule } from './modules/platform/errors/errors.module';
import type { HomeModule } from './modules/home';
import type { RifModule } from './modules/accounts/rif/rif.module';
import type { BalancesModule } from './modules/balances/balances.module';
import type { AddToWatchlistModule, WatchlistModule } from './modules/watchlist/watchlist.module';
import type { HardToBorrowModule } from './modules/fpl/hard-to-borrow/hard-to-borrow.module';
import type { FplEnrollmentModule } from './modules/fpl/enrollment/fpl-enrollment.module';
import type { W8BenFormModule } from './modules/w8ben-form/w8ben-form.module';
import type { GamlAttestationModule } from './modules/gaml-attestation/gaml-attestation.module';
import type { LtiModule } from './modules/lti/lti.module';
import type { PreferencesModule } from './modules/platform/preferences/preferences.module';
import type { ThematicInvestingModule } from './modules/thematic-investing/thematic-investing.module';
import type { AccountsModule } from './modules/accounts';

export class WebbrokerRoutes extends BaseRoutes {
  public static useHash: boolean = true;

  public static routes: StatesList = [
    {
      name: 'page.blank',
      url: '/blank'
    },
    {
      name: 'page.errors.**',
      url: '/errors',
      loadChildren: (): LazyLoad<ErrorsModule> => import('./modules/platform/errors/errors.module')
        .then((m: typeof import('./modules/platform/errors/errors.module')): typeof ErrorsModule => m.ErrorsModule)
    },
    {
      name: 'page.home.**',
      url: '/home',
      loadChildren: (): LazyLoad<HomeModule> => import('./modules/home/home.module')
        .then((m: typeof import('./modules/home/home.module')): typeof HomeModule => m.HomeModule)
    },
    {
      name: 'page.account.**',
      url: '/account',
      loadChildren: (): LazyLoad<AccountsModule> => import('./modules/accounts/accounts.module')
        .then((m: typeof import('./modules/accounts/accounts.module')): typeof AccountsModule => m.AccountsModule)
    },
    {
      name: 'page.rrif.**',
      url: '/rrif',
      loadChildren: (): LazyLoad<RifModule> => import('./modules/accounts/rif/rif.module')
        .then((m: typeof import('./modules/accounts/rif/rif.module')): typeof RifModule => m.RifModule)
    },
    {
      name: 'page.balances.**',
      url: '/balances',
      loadChildren: (): LazyLoad<BalancesModule> => import('./modules/balances/balances.module')
        .then((m: typeof import('./modules/balances/balances.module')): typeof BalancesModule => m.BalancesModule)
    },
    {
      name: 'modal.watchlist.**',
      url: '/markets/watchlist',
      loadChildren: (): LazyLoad<WatchlistModule> => import('./modules/watchlist/watchlist.module')
        .then((m: typeof import('./modules/watchlist/watchlist.module')): typeof WatchlistModule => m.WatchlistModule)
    },
    {
      name: 'modal.add-watchlist.**',
      url: '/add-watchlist',
      loadChildren: (): LazyLoad<AddToWatchlistModule> => import('./modules/watchlist/watchlist.module')
        .then((m: typeof import('./modules/watchlist/watchlist.module')): typeof AddToWatchlistModule => m.AddToWatchlistModule)
    },
    {
      name: 'page.lti.**',
      url: '/lti',
      loadChildren: (): LazyLoad<LtiModule> => import('./modules/lti/lti.module')
        .then((m: typeof import('./modules/lti/lti.module')): typeof LtiModule => m.LtiModule)
    },
    {
      name: 'modal.lti.**',
      url: '/lti',
      loadChildren: (): LazyLoad<LtiModule> => import('./modules/lti/lti.module')
        .then((m: typeof import('./modules/lti/lti.module')): typeof LtiModule => m.LtiModule)
    },
    {
      name: 'modal.messages',
      url: '/messages',
      component: MessagesModalComponent
    },
    {
      name: 'page.preferences.**',
      url: '/preferences',
      loadChildren: (): LazyLoad<PreferencesModule> => import('./modules/platform/preferences/preferences.module')
        .then((m: typeof import('./modules/platform/preferences/preferences.module')): typeof PreferencesModule => m.PreferencesModule)
    },
    {
      name: 'page.attrader.**',
      url: '/attrader',
      loadChildren: (): LazyLoad<AttraderModule> => import('./modules/platform/preferences/attrader/attrader.module')
        .then((m: typeof import('./modules/platform/preferences/attrader/attrader.module')): typeof AttraderModule => m.AttraderModule)
    },
    {
      name: 'page.new-user.**',
      url: '/platform/new-user',
      loadChildren: (): LazyLoad<NewUserModule> => import('./modules/platform/new-user/new-user.module')
        .then((m: typeof import('./modules/platform/new-user/new-user.module')): typeof NewUserModule => m.NewUserModule)
    },
    {
      name: 'modal.transfers.**',
      url: '/transfers',
      loadChildren: (): LazyLoad<TransfersModule> => import('./modules/transfers/transfers.module')
        .then((m: typeof import('./modules/transfers/transfers.module')): typeof TransfersModule => m.TransfersModule),
      redirectTo: 'modal.transfers.cash'
    },
    {
      name: 'page.transfers.**',
      url: '/transfers',
      loadChildren: (): LazyLoad<TransfersModule> => import('./modules/transfers/transfers.module')
        .then((m: typeof import('./modules/transfers/transfers.module')): typeof TransfersModule => m.TransfersModule)
    },
    {
      name: 'page.eservices.**',
      url: '/eservices',
      loadChildren: (): LazyLoad<EservicesModule> => import('./modules/eservices/eservices.module')
        .then((m: typeof import('./modules/eservices/eservices.module')): typeof EservicesModule => m.EservicesModule),
      redirectTo: 'page.eservices.registered.statements'
    },
    {
      name: 'modal.eservices.**',
      url: '/eservices',
      loadChildren: (): LazyLoad<EservicesModule> => import('./modules/eservices/eservices.module')
        .then((m: typeof import('./modules/eservices/eservices.module')): typeof EservicesModule => m.EservicesModule)
    },
    {
      name: 'modal.add-symbol-to-watchlist',
      url: '/markets/add-watchlist-symbol?{symbol:string}&{market:string}&{symbolType:string}',
      redirectTo: 'modal.add-watchlist.symbol'
    },
    {
      name: 'page.research',
      url: '/research?DESTINATION&PARTNER_FIELDS&top&uuid',
      params: {
        DESTINATION: 'MOD',
        PARTNER_FIELDS: {
          value: 'markets/overview',
          array: false,
          raw: true
        }
      },
      views: {
        '@': {
          component: IntegrationComponent
        }
      }
    },
    {
      name: 'page.new-issues',
      url: '/new-issues',
      views: {
        '@': {
          component: IntegrationComponent
        }
      }
    },
    {
      name: 'page.fixed-income',
      url: '/fixed-income?orderAction&orderSubAction&orderQty&cusip&accountId',
      views: {
        '@': {
          component: IntegrationComponent
        }
      }
    },
    {
      name: 'page.learn.**',
      url: '/learn',
      loadChildren: (): LazyLoad<LearnModule> => import('./modules/learn/learn.module')
        .then((m: typeof import('./modules/learn/learn.module')): typeof LearnModule => m.LearnModule)
    },
    {
      name: 'modal.learn.**',
      url: '/learn',
      loadChildren: (): LazyLoad<LearnModule> => import('./modules/learn/learn.module')
        .then((m: typeof import('./modules/learn/learn.module')): typeof LearnModule => m.LearnModule)
    },
    {
      name: 'page.help',
      url: '/help/:articleId',
      params: {
        articleId: {
          squash: true,
          value: null
        }
      }
    },
    {
      name: 'page.unsupported',
      url: '/unsupported',
      views: {
        '@': {
          component: UnsupportedBrowserComponent
        }
      },
      params: {
        to: {
          type: 'string'
        },
        params: {
          type: 'any'
        }
      },
      resolve: [
        { token: 'to', deps: [Transition], resolveFn: WebbrokerRoutes.getToRoute },
        { token: 'params', deps: [Transition], resolveFn: WebbrokerRoutes.getToParams }
      ]
    },
    // 'page.webbroker.**' is not authorized in production mode, see WebbrokerConfig.getLinkRegistryConfig().
    {
      name: 'page.webbroker.**',
      url: '/webbroker',
      loadChildren: (): LazyLoad<WebbrokerCoreE2EModule> => import('./modules/core/sandbox/webbroker-core-e2e.module')
        .then((m: typeof import('./modules/core/sandbox/webbroker-core-e2e.module')): typeof WebbrokerCoreE2EModule => m.WebbrokerCoreE2EModule)
    },
    {
      name: 'page.order-status.**',
      url: '/trading/order-status',
      loadChildren: (): LazyLoad<OrderStatusModule> => import('./modules/trading/order-status/order-status.module')
        .then((m: typeof import('./modules/trading/order-status/order-status.module')): typeof OrderStatusModule => m.OrderStatusModule)
    },
    {
      name: 'page.gic-rate-sheet.**',
      url: '/gic-rate-sheet',
      loadChildren: (): LazyLoad<GicPageModule> => import('./modules/trading/gic/gic-page.module')
        .then((m: typeof import('./modules/trading/gic/gic-page.module')): typeof GicPageModule => m.GicPageModule)
    },
    {
      name: 'page.fractional-landing.**',
      url: '/fractional-landing',
      loadChildren: (): LazyLoad<FractionalModule> => import('./modules/trading/fractional/fractional.module')
        .then((m: typeof import('./modules/trading/fractional/fractional.module')): typeof FractionalModule => m.FractionalModule)
    },
    {
      name: 'page.profile.**',
      url: '/profile',
      loadChildren: (): LazyLoad<UserProfileModule> => import('./modules/user-profile/user-profile.module')
        .then((m: typeof import('./modules/user-profile/user-profile.module')): typeof UserProfileModule => m.UserProfileModule)
    },
    {
      name: 'page.ad-enroll.**',
      url: '/ad-enroll',
      loadChildren: (): LazyLoad<AdEnrollModule> => import('./modules/ad-enroll/ad-enroll.module')
        .then((m: typeof import('./modules/ad-enroll/ad-enroll.module')): typeof AdEnrollModule => m.AdEnrollModule)
    },
    {
      name: 'page.secure-messages.**',
      url: '/secure-message',
      loadChildren: (): LazyLoad<SecureMessagesModule> => import('./modules/platform/secure-messages/secure-messages.module')
        .then((m: typeof import('./modules/platform/secure-messages/secure-messages.module')): typeof SecureMessagesModule => m.SecureMessagesModule)
    },
    {
      name: 'modal.w8ben.**',
      url: '/w8ben',
      loadChildren: (): LazyLoad<W8BenFormModule> => import('./modules/w8ben-form/w8ben-form.module')
        .then((m: typeof import('./modules/w8ben-form/w8ben-form.module')): typeof W8BenFormModule => m.W8BenFormModule)
    },
    {
      name: 'modal.gaml.**',
      url: '/gaml',
      loadChildren: (): LazyLoad<GamlAttestationModule> => import('./modules/gaml-attestation/gaml-attestation.module')
        .then((m: typeof import('./modules/gaml-attestation/gaml-attestation.module')): typeof GamlAttestationModule => m.GamlAttestationModule)
    },
    {
      name: 'page.hard-to-borrow.**',
      url: '/hard-to-borrow',
      loadChildren: (): LazyLoad<HardToBorrowModule> => import('./modules/fpl/hard-to-borrow/hard-to-borrow.module')
        .then((m: typeof import('./modules/fpl/hard-to-borrow/hard-to-borrow.module')): typeof HardToBorrowModule => m.HardToBorrowModule)
    },
    {
      name: 'modal.fpl-enrollment.**',
      url: '/fpl/enrollment',
      loadChildren: (): LazyLoad<FplEnrollmentModule> => import('./modules/fpl/enrollment/fpl-enrollment.module')
        .then((m: typeof import('./modules/fpl/enrollment/fpl-enrollment.module')): typeof FplEnrollmentModule => m.FplEnrollmentModule)
    },
    {
      name: 'page.thematic-investing.**',
      url: '/thematic-investing',
      loadChildren: (): LazyLoad<ThematicInvestingModule> => import('./modules/thematic-investing/thematic-investing.module')
        .then((m: typeof import('./modules/thematic-investing/thematic-investing.module')): typeof ThematicInvestingModule => m.ThematicInvestingModule)
    }
  ];

  static orderEntryRouteState: Ng2StateDeclaration = {
    name: 'modal.trading.**',
    url: '/trading',
    loadChildren: (): LazyLoad<OrderEntryModule> => import('./modules/trading/order-entry-reorg/order-entry.module')
      .then((m: typeof import('./modules/trading/order-entry-reorg/order-entry.module')): typeof OrderEntryModule => m.OrderEntryModule),
    redirectTo: 'modal.trading.order'
  };

  static configure(uiRouter: UIRouter): void {
    BaseRoutes.configure(uiRouter);
  }

  static getToRoute(transition: Transition): string {
    return transition.params().to;
  }

  static getToParams(transition: Transition): string {
    return transition.params().params;
  }
}
